/**
 * ExploreCategoryPreview - Default category preview with entries
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { Suspense, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { AlternatingCategoryRow, getStyleForIndex, CategoryStyle } from './AlternatingCategoryRow';
import { useCategoryEntriesSuspense } from '../../Hooks/useCategoryEntriesSuspense';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const CategorySection = styled.div<{ isLast?: boolean }>`
  padding: 48px 0;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #f0f0f0')};
  animation: ${fadeInUp} 0.6s ease-out;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const CategoryInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const CategoryTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
  line-height: 1.2;
`;

const CategoryDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
`;

const ShowAllLink = styled.a`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const StyleIndicator = styled.div<{ cardStyle: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ cardStyle }) => {
    switch (cardStyle) {
      case 'cinematic':
        return 'linear-gradient(135deg, #00dda2, #007763)';
      case 'tiles':
        return 'linear-gradient(135deg, #007763, #007763)';
      case 'magazine':
        return 'linear-gradient(135deg, #00AF96, #007763)';
      case 'portrait':
        return 'linear-gradient(135deg, #00dda2, #00AF96)';
      default:
        return '#00dda2';
    }
  }};
`;

// Skeleton components
const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '14px'};
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 100px;
  height: 44px;
  border-radius: 24px;
`;

// Cinematic skeleton
const CinematicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const CinematicCardSkeleton = styled(SkeletonBase)`
  aspect-ratio: 16 / 9;
  border-radius: 16px;
`;

// Tiles skeleton
const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;
const TileCardSkeleton = styled(SkeletonBase)`
  aspect-ratio: 1;
  border-radius: 20px;
`;

// Magazine skeleton
const MagazineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const MagazineCardSkeleton = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  height: 180px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  @media (max-width: 600px) { height: 140px; }
`;
const MagazineImageSkeleton = styled(SkeletonBase)`
  width: 40%;
  min-width: 140px;
  border-radius: 0;
`;
const MagazineContentSkeleton = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`;

// Portrait skeleton
const PortraitScrollContainer = styled.div`
  display: flex;
  gap: 20px;
  overflow: hidden;
  padding: 16px 0;
`;
const PortraitCardSkeleton = styled(SkeletonBase)`
  flex: 0 0 200px;
  aspect-ratio: 3 / 4;
  border-radius: 20px;
  @media (max-width: 600px) { flex: 0 0 160px; }
`;

const CategorySkeletonRow: React.FC<{ style: CategoryStyle }> = ({ style }) => {
  switch (style) {
    case 'cinematic':
      return (
        <CinematicGrid>
          {Array.from({ length: 4 }).map((_, i) => <CinematicCardSkeleton key={i} />)}
        </CinematicGrid>
      );
    case 'tiles':
      return (
        <TilesGrid>
          {Array.from({ length: 8 }).map((_, i) => <TileCardSkeleton key={i} />)}
        </TilesGrid>
      );
    case 'magazine':
      return (
        <MagazineGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <MagazineCardSkeleton key={i}>
              <MagazineImageSkeleton />
              <MagazineContentSkeleton>
                <SkeletonText width="30%" height="3px" />
                <SkeletonText width="40%" height="11px" />
                <SkeletonText width="80%" height="18px" />
                <SkeletonText width="100%" height="14px" />
              </MagazineContentSkeleton>
            </MagazineCardSkeleton>
          ))}
        </MagazineGrid>
      );
    case 'portrait':
      return (
        <PortraitScrollContainer>
          {Array.from({ length: 6 }).map((_, i) => <PortraitCardSkeleton key={i} />)}
        </PortraitScrollContainer>
      );
    default:
      return null;
  }
};

type CategorySectionContentProps = {
  category: ExploreCategory;
  categoryIndex: number;
  isLast?: boolean;
  linkPrefix: string;
};

// Suspends while loading — hides entire section when no entries
const CategorySectionContent: React.FC<CategorySectionContentProps> = observer(
  ({ category, categoryIndex, isLast, linkPrefix }) => {
    const { t } = useTrans();
    const navigate = useNavigate();
    const categoryTitle = useLocalized(category.name);
    const shortDescription = useLocalized(category.shortDescription);
    const longDescription = useLocalized(category.description);
    const description = shortDescription || longDescription;
    const categoryStyle = getStyleForIndex(categoryIndex);
    const { entries } = useCategoryEntriesSuspense(category.id, category.identifier);

    const handleSeeAll = useCallback(() => {
      // The Webinar library category shold point to the whole webinar library, not just the category page
      if (category.identifier === 'fc004273-c8f1-45fd-bc8a-6c9398ed4f7a') {
        navigate('/academy/library');
        return;
      }
      navigate(`${linkPrefix}${category.identifier}`);
    }, [navigate, linkPrefix, category.identifier]);

    if (entries.length === 0) {
      return null;
    }

    return (
      <CategorySection isLast={isLast}>
        <Container>
          <CategoryHeader>
            <CategoryInfo>
              <CategoryTitle>
                {categoryTitle}
              </CategoryTitle>
              {description && <CategoryDescription>{description}</CategoryDescription>}
            </CategoryInfo>
            <ShowAllLink onClick={handleSeeAll}>{t('globals.showAll', { defaultValue: 'Show All' })}</ShowAllLink>
          </CategoryHeader>
          <AlternatingCategoryRow entries={entries} cardStyle={categoryStyle} linkPrefix={linkPrefix} />
        </Container>
      </CategorySection>
    );
  },
);

export type ExploreCategoryPreviewProps = {
  category: ExploreCategory;
  categoryIndex?: number;
  isLast?: boolean;
  linkPrefix?: string;
};

export const ExploreCategoryPreview: React.FC<ExploreCategoryPreviewProps> = observer(
  ({ category, categoryIndex = 0, isLast, linkPrefix = '/' }) => {
    const categoryStyle = getStyleForIndex(categoryIndex);

    return (
      <Suspense
        fallback={
          <CategorySection>
            <Container>
              <CategorySkeletonRow style={categoryStyle} />
            </Container>
          </CategorySection>
        }
      >
        <CategorySectionContent
          category={category}
          categoryIndex={categoryIndex}
          isLast={isLast}
          linkPrefix={linkPrefix}
        />
      </Suspense>
    );
  },
);
